/* =============================================================================
   SMOKEY'S CHECKOUT FENCE
   =============================================================================
   ensureCanonicalCartContext() protects /cart/add, but CHECKOUT was outside that
   fence. The pre-existing interceptor lives in cart-delivery.js and validates
   DELIVERY state, not the PURP/Greenline identity contract: a pickup (or any
   non-delivery) cart proceeded straight through, and its error fallback also
   proceeded. cart-delivery.js is additionally loaded lazily on idle, so a
   cart-drawer or cart-notification checkout could be clicked before it exists.

   Because cart.attributes.store_id decides whether PURP or the legacy Greenline
   connector OWNS the resulting order, checkout must re-verify identity. The same
   fresh cart is then passed through the shared delivery-minimum policy.

   This module is loaded EAGERLY beside the cart-add fence and awaits a FRESH
   ensureCanonicalCartContext() before permitting:
     - cart page checkout form submission (button, and Enter-key submit)
     - cart drawer checkout
     - cart notification checkout
     - any direct internal /checkout link or form
   Identity failures remain fail-closed. The minimum check only blocks a cart when
   delivery and an under-minimum merchandise subtotal are both proven; pickup or
   unreadable/ambiguous delivery state is never newly blocked by this policy.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__smokeysCheckoutGuardInstalled) return;
  window.__smokeysCheckoutGuardInstalled = true;

  /**
   * THE promise-attribute allowlist — one definition for the whole theme.
   *
   * A stale receipt is worse than none: an old Express promise left on the cart can
   * block a pickup checkout, or be ingested as a regular order's narrow delivery
   * window. Several different files remove a delivery selection (express deselect,
   * scheduled-window clear, address change, store/pickup switch), so the key set has to
   * live somewhere ALL of them can reach — and this file is loaded eagerly on every
   * page. Every removal path spreads `PurpPromiseAttributes.cleared()`; none of them
   * keeps its own copy of the list.
   */
  window.PurpPromiseAttributes = window.PurpPromiseAttributes || (function () {
    var KEYS = [
      'PURP Promise Receipt',
      'PURP Promise Version',
      'PURP Promise Quote',
      'PURP Promise Expires',
      'PURP Promise Kind',
      'PURP Promise Window'
    ];
    return {
      KEYS: KEYS.slice(),
      /** Shopify clears an attribute with '' (never null). */
      cleared: function () {
        var out = {};
        for (var i = 0; i < KEYS.length; i += 1) out[KEYS[i]] = '';
        return out;
      }
    };
  })();

  var BUSY_ATTR = 'data-smokeys-checkout-verifying';
  var POLICY_DONE_ATTR = 'data-smokeys-checkout-policy-verified';

  function ensureContext() {
    var ctx = window.SmokeysCartContext;
    if (ctx && typeof ctx.ensureCanonicalCartContext === 'function') {
      return ctx.ensureCanonicalCartContext();
    }
    var e = new Error('Store context unavailable. Please refresh and try again.');
    e.userMessage = e.message;
    return Promise.reject(e);
  }

  function findErrorTarget(scope, anchor) {
    var roots = [];
    try {
      if (anchor && anchor.closest) {
        roots.push(anchor.closest('cart-drawer, cart-notification, #main-cart-footer'));
        roots.push(anchor.closest('form'));
      }
      if (scope && scope.closest) {
        roots.push(scope.closest('cart-drawer, cart-notification, #main-cart-footer'));
        roots.push(scope.closest('form'));
      }
    } catch (e) {}
    roots.push(document);

    for (var i = 0; i < roots.length; i += 1) {
      var root = roots[i];
      if (!root || !root.querySelector) continue;
      var target = root.querySelector(
        '[data-smokeys-checkout-error], .cart__error, .product-form__error-message-wrapper'
      );
      if (target) return target;
    }
    return null;
  }

  function clearError(scope, anchor) {
    var target = findErrorTarget(scope, anchor);
    if (!target) return;
    target.textContent = '';
    target.setAttribute('hidden', '');
  }

  function showError(scope, anchor, message) {
    try {
      var target = findErrorTarget(scope, anchor);
      if (target) {
        target.textContent = message;
        target.removeAttribute('hidden');
        return;
      }
    } catch (e) {}
    if (window.console && console.warn) console.warn('[Checkout] ' + message);
  }

  function verifyCheckout() {
    return ensureContext().then(function (context) {
      var policy = window.SmokeysDeliveryMinimum;
      if (!policy || typeof policy.evaluateCheckout !== 'function') {
        return { context: context };
      }

      var result;
      try {
        result = policy.evaluateCheckout(context && context.cart);
      } catch (e) {
        // The identity fence already verified the cart. A policy-evaluator fault
        // must not strand a legitimate checkout.
        if (window.console && console.warn) {
          console.warn('[Checkout] Delivery minimum evaluator unavailable; allowing the verified checkout.');
        }
        return { context: context };
      }

      // Pickup is retailer-owned and never enters smart-delivery admission, even if
      // a stale receipt somehow survived a third-party cart mutation.
      if (!result.applicable) {
        return { context: context, deliveryMinimum: result };
      }

      if (result.allowed) {
        return { context: context, deliveryMinimum: result };
      }

      var error = new Error(policy.messageFor(result));
      error.code = 'delivery_minimum_not_met';
      error.userMessage = error.message;
      error.deliveryMinimum = result;
      throw error;
    });
  }

  function isCheckoutSubmitter(el) {
    if (!el) return false;
    if (el.name === 'checkout') return true;
    if (el.id === 'checkout' || el.id === 'CartDrawer-Checkout') return true;
    if (el.matches && el.matches('[name="checkout"], .cart__checkout-button, #checkout, #CartDrawer-Checkout')) return true;
    return false;
  }

  // Same-origin /checkout, resolved absolutely so an absolute same-origin URL is
  // treated the same as a relative one.
  function isCheckoutUrl(raw) {
    if (!raw) return false;
    try {
      var u = new URL(raw, window.location.origin);
      if (u.origin !== window.location.origin) return false;
      return /^\/checkout(\/|\?|$)/.test(u.pathname) || /^\/cart\/checkout/.test(u.pathname);
    } catch (e) { return false; }
  }

  function isCheckoutLink(a) {
    return !!a && a.tagName === 'A' && isCheckoutUrl(a.getAttribute('href'));
  }

  // A form is a checkout form when it posts to /checkout, is explicitly marked,
  // or is the cart form being submitted by a checkout button.
  function isCheckoutForm(form, submitter) {
    if (!form) return false;
    if (form.hasAttribute && form.hasAttribute('data-smokeys-checkout-form')) return true;
    var action = String(form.action || '');
    if (isCheckoutUrl(action)) return true;
    var isCartForm = /\/cart\b/.test(action) && !/\/cart\/add/.test(action);
    return isCartForm && isCheckoutSubmitter(submitter);
  }

  function refusalSurface(node, anchor) {
    var element = anchor || node;
    try {
      if (element && element.tagName === 'A') return 'checkout_link';
      if (element && element.closest && element.closest('cart-drawer')) return 'cart_drawer';
      if (element && element.closest && element.closest('cart-notification')) return 'cart_notification';
    } catch (e) {}
    return 'cart';
  }

  function reportCheckoutRefusal(error, node, anchor) {
    try {
      var policy = window.SmokeysDeliveryMinimum;
      if (!policy || typeof policy.reportMinimumRefusal !== 'function') return;
      policy.reportMinimumRefusal({
        decisionPoint: 'checkout_attempt',
        surface: refusalSurface(node, anchor),
        deliveryKind: error && error.deliveryMinimum && error.deliveryMinimum.deliveryKind,
        eligibility: error && error.deliveryMinimum
      });
    } catch (e) {}
  }

  function guard(e, node, anchor, proceed) {
    // Already verified this gesture -> let it through.
    if (node && node.getAttribute && node.getAttribute(BUSY_ATTR) === 'done') {
      node.removeAttribute(BUSY_ATTR);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Repeated clicks must not attach several proceed() callbacks to one
    // in-flight verification.
    if (node && node.getAttribute && node.getAttribute(BUSY_ATTR) === 'pending') return;
    if (node && node.setAttribute) node.setAttribute(BUSY_ATTR, 'pending');
    clearError(node, anchor);

    verifyCheckout()
      .then(function () {
        if (node && node.setAttribute) {
          node.setAttribute(BUSY_ATTR, 'done');
          // The older cart-delivery fence still performs slot-integrity checks. Give
          // it a one-shot proof that the shared minimum policy already ran.
          node.setAttribute(POLICY_DONE_ATTR, 'done');
        }
        proceed();
      })
      .catch(function (error) {
        if (node && node.removeAttribute) node.removeAttribute(BUSY_ATTR);
        showError(node, anchor, (error && error.userMessage) || 'We could not confirm your store. Please try again.');
        if (error && error.code === 'delivery_minimum_not_met') {
          reportCheckoutRefusal(error, node, anchor);
        }
      });
  }

  // --- cart / drawer form submission (covers button click AND Enter key) ------
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form) return;
      // /cart/add is fenced separately by cart-add-guards.js
      if (/\/cart\/add/.test(String(form.action || ''))) return;

      var submitter = e.submitter || form.__smokeysCheckoutSubmitter;
      form.__smokeysCheckoutSubmitter = null;
      if (!submitter || !isCheckoutSubmitter(submitter)) {
        var active = document.activeElement;
        submitter = isCheckoutSubmitter(active) ? active : null;
      }
      if (!isCheckoutForm(form, submitter)) return;

      guard(e, form, submitter, function () {
        var validSubmitter = submitter;
        if (!validSubmitter && form.id && document.querySelector) {
          validSubmitter = document.querySelector(
            '[form="' + form.id + '"][name="checkout"], [form="' + form.id + '"]#checkout'
          );
        }
        if (typeof form.requestSubmit === 'function') {
          if (validSubmitter) form.requestSubmit(validSubmitter);
          else form.requestSubmit();
        }
        else form.submit();
      });
    },
    true
  );

  // --- direct checkout buttons outside a form, and /checkout links -----------
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('button, input[type="submit"], a');
      if (!btn) return;

      if (btn.tagName === 'A') {
        if (!isCheckoutLink(btn)) return;
        guard(e, btn, btn, function () { window.location.href = btn.href; });
        return;
      }

      if (!isCheckoutSubmitter(btn)) return;
      // If it belongs to a form the submit handler above fences it. Remember the
      // owner for Safari versions that omit SubmitEvent.submitter.
      var ownerForm = btn.form || (btn.closest && btn.closest('form'));
      if (ownerForm) {
        ownerForm.__smokeysCheckoutSubmitter = btn;
        return;
      }
      guard(e, btn, btn, function () { btn.click(); });
    },
    true
  );

  window.SmokeysCheckoutGuard = Object.freeze({
    isCheckoutForm: isCheckoutForm,
    isCheckoutUrl: isCheckoutUrl,
    verifyCheckout: verifyCheckout
  });
})();
